import {useMutation, useQuery} from '@apollo/client'
import {FormikErrors, FormikTouched} from 'formik'
import {useEffect, useState} from 'react'
import {Button, Col, Form, InputGroup, Row, Spinner} from 'react-bootstrap'
import { DateInput } from 'app/tfi/components/DateInput'
import {useSearchParams} from 'react-router-dom'
import {Tooltip} from 'react-tooltip'
import {IResStates} from '../../../../types/statesEU'
import {GET_STATES_EU} from '../../../../gql/queries/statesEUQuery'
import {toast} from 'react-toastify'
import {
  TFormAllValues,
  TFormInsurancePolicy,
  TFormPaymentsPolicy,
} from '../../../policies/policiesTypes'
import {MONTHS, ALL_PERMISSIONS} from '../../../../helpers/consts'
import {parseId} from '../../../../helpers'
import {getStateEUByCode, getStateEUById} from '../../../shared/utils'
import {LOG_POLICY_PAYMENT_VIEW} from '../../../../gql/mutations/policyPaymentViewMutation'
import {hasPermission} from 'app/tfi/helpers'
import {useGetUser} from 'app/tfi/store/userStore'

interface IInsurancePayment {
  onPrevStep: () => void
  onNextStep: () => void
  handleChange: (field: string, value: string | boolean) => void
  handleBlur: (field: string) => void
  allValues: TFormPaymentsPolicy
  errors: FormikErrors<TFormPaymentsPolicy>
  touched: FormikTouched<TFormPaymentsPolicy>
  policyId?: string
}

export const InsurancePaymentsTab: React.FC<IInsurancePayment> = ({
  onPrevStep,
  onNextStep,
  handleBlur,
  handleChange,
  allValues,
  errors,
  touched,
  policyId,
}) => {
  // console.log('policyPayment:', allValues)
  const {data: dataStates, error: errorStates} = useQuery<IResStates>(GET_STATES_EU)

  const [showCardNum, setShowCardNum] = useState(false)
  const [showCvvCsv, setShowCvvCsv] = useState(false)
  const [showABA, setShowABA] = useState(false)
  const [showAccountNum, setShowAccountNum] = useState(false)

  // Mutation to log payment view
  const [logPaymentView] = useMutation(LOG_POLICY_PAYMENT_VIEW)

  // Get permissions from localStorage
  const permissions = useGetUser((s) => s.user?.permissions)
  const hasViewPaymentPermission = permissions && hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY_PAYMENT)

  useEffect(() => {
    if (errorStates) toast.error(`Error: ${errorStates.message}`)
  }, [errorStates])

  // Helper function to log payment view and notify supervisor
  const logPaymentDataView = async (fieldViewed: string) => {
    console.log('=== LOG PAYMENT VIEW START ===')
    console.log('policyId:', policyId)
    console.log('fieldViewed:', fieldViewed)
    console.log('hasViewPaymentPermission:', hasViewPaymentPermission)

    if (!policyId) {
      console.warn('No policyId provided, skipping log')
      return
    }

    try {
      console.log('Calling logPaymentView mutation with variables:', {
        policyId: policyId,
        fieldViewed: fieldViewed,
      })

      const {data} = await logPaymentView({
        variables: {
          policyId: parseInt(policyId, 10),
          fieldViewed: fieldViewed,
        },
      })

      console.log('Mutation response:', data)

      if (data?.logPolicyPaymentView?.success) {
        console.log(`✅ Payment view logged successfully: ${fieldViewed}`)
      } else if (data?.logPolicyPaymentView?.errors) {
        console.error('❌ Error logging payment view:', data.logPolicyPaymentView.errors)
      }
    } catch (error: any) {
      console.error('❌ Exception calling logPaymentView mutation:', error)
      console.error('Error message:', error.message)
      console.error('Network error:', error.networkError)
      console.error('GraphQL errors:', error.graphQLErrors)
      if (error.networkError?.result) {
        console.error('Network error result:', error.networkError.result)
      }
    }

    console.log('=== LOG PAYMENT VIEW END ===')
  }

  const toggleCardNum = () => {
    const newState = !showCardNum
    console.log('🔵 toggleCardNum called - newState:', newState, 'hasViewPaymentPermission:', hasViewPaymentPermission)
    setShowCardNum(newState)
    // Log when showing the card number (only if user doesn't have permission and field has data)
    if (newState && !hasViewPaymentPermission && allValues.cardNumber) {
      console.log('🟢 Calling logPaymentDataView for card_number')
      logPaymentDataView('card_number')
    } else {
      console.log('🔴 Not logging - either hiding, user has permission, or field is empty')
    }
  }

  const toggleCvvCsv = () => {
    const newState = !showCvvCsv
    console.log('🔵 toggleCvvCsv called - newState:', newState, 'hasViewPaymentPermission:', hasViewPaymentPermission)
    setShowCvvCsv(newState)
    // Log when showing the CVV (only if user doesn't have permission and field has data)
    if (newState && !hasViewPaymentPermission && allValues.cvvCsv) {
      console.log('🟢 Calling logPaymentDataView for cvv')
      logPaymentDataView('cvv')
    } else {
      console.log('🔴 Not logging - either hiding, user has permission, or field is empty')
    }
  }

  const toggleABA = () => {
    const newState = !showABA
    console.log('🔵 toggleABA called - newState:', newState, 'hasViewPaymentPermission:', hasViewPaymentPermission)
    setShowABA(newState)
    // Log when showing ABA/Routing (only if user doesn't have permission and field has data)
    if (newState && !hasViewPaymentPermission && allValues.ABARouting) {
      console.log('🟢 Calling logPaymentDataView for aba_routing')
      logPaymentDataView('aba_routing')
    } else {
      console.log('🔴 Not logging - either hiding, user has permission, or field is empty')
    }
  }

  const toggleAccountNum = () => {
    const newState = !showAccountNum
    console.log('🔵 toggleAccountNum called - newState:', newState, 'hasViewPaymentPermission:', hasViewPaymentPermission)
    setShowAccountNum(newState)
    // Log when showing account number (only if user doesn't have permission and field has data)
    if (newState && !hasViewPaymentPermission && allValues.accountNumber) {
      console.log('🟢 Calling logPaymentDataView for account_number')
      logPaymentDataView('account_number')
    } else {
      console.log('🔴 Not logging - either hiding, user has permission, or field is empty')
    }
  }

  const stateEUId =
    typeof allValues.stateEU === 'object' && allValues.stateEU !== null
      ? String((allValues.stateEU as any).id)
      : allValues.stateEU == null
        ? ''
        : String(allValues.stateEU);

  console.log('allStates', dataStates?.allStates)
  console.log('stateEU',  allValues.stateEU)
  console.log('getStateEUById', getStateEUById(dataStates?.allStates, String(stateEUId)));

  return (
    <div className='policy-insurance-container'>
      <div className='kt-heading kt-heading--md custom-border-brand mt-5'>Payment information</div>
      <Row>
        <Col md={4} sm={12}>
          <Form.Group>
            <Form.Label style={{fontSize: '12px'}}>Recurrent payment?</Form.Label>
            <div className='d-flex gap-5 mt-3'>
              <Form.Check
                type='radio'
                name='recurrentPayment'
                label='Yes'
                value='true'
                onChange={(e) => handleChange('recurrentPayment', true)}
                onBlur={() => handleBlur('recurrentPayment')}
                checked={allValues.recurrentPayment=== true}
              />
              <Form.Check
                type='radio'
                name='recurrentPayment'
                label='No'
                value='false'
                onChange={(e) => handleChange('recurrentPayment', false)}
                onBlur={() => handleBlur('recurrentPayment')}
                checked={allValues.recurrentPayment === false}
              />
            </div>
          </Form.Group>
        </Col>
        <Col md={4} sm={12}>
          <DateInput
            id="firstPaymentDate"
            name="firstPaymentDate"
            label="First payment date"
            value={allValues.firstPaymentDate}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Col>
        <Col md={4} sm={12}>
          <Form.Group>
            <Form.Label htmlFor='preferredPayment' style={{fontSize: '12px'}}>
              Preferred payment day each month
            </Form.Label>
            <Form.Select
              aria-label='Default select example'
              id='preferredPayment'
              name='preferredPayment'
              onChange={(e) => handleChange('preferredPayment', e.target.value)}
              onBlur={() => handleBlur('preferredPayment')}
              value={allValues.preferredPayment}
            >
              <option value=''>Open this select menu</option>
              {Array.from({length: 31}).map((_, n) => (
                <option value={n + 1} key={n}>
                  {n + 1}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className='mt-5 pb-4'>
        <Col md={4} sm={12}>
          <h5 className='font-bold py-2 fs-4' style={{borderBottom: '1px solid lightgray'}}>
            Credit/Debit Card
          </h5>
          <Form.Group>
            <div className='d-flex gap-2 mt-3'>
              <Form.Check
                type='radio'
                name='typeCard'
                onChange={() => handleChange('typeCard', 'CREDIT_CARD')}
                onBlur={() => handleBlur('typeCard')}
                value={allValues.typeCard}
                label='Credit Card'
                checked={allValues.typeCard === 'CREDIT_CARD'}
              />
              <Form.Check
                type='radio'
                name='typeCard'
                onChange={() => handleChange('typeCard', 'DEBIT_CARD')}
                onBlur={() => handleBlur('typeCard')}
                value={allValues.typeCard}
                checked={allValues.typeCard === 'DEBIT_CARD'}
                label='Debit Card'
              />
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label style={{fontSize: '12px'}}>Card company</Form.Label>
            <Form.Select
              id='cardCompany'
              name='cardCompany'
              value={allValues.cardCompany}
              onChange={(e) => {
                handleChange('cardCompany', e.target.value)
              }}
              aria-label='Card company'
            >
              <option value=''>Open this select menu</option>
              {['Visa', 'Master Card', 'Discover', 'Amex'].map((st) => (
                <option value={st} key={st}>
                  {st}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div>
            <Form.Label>Card number</Form.Label>
            <InputGroup>
              <Form.Control
                placeholder='Only numbers allowed'
                aria-label='Only numbers allowed'
                aria-describedby='Card number'
                type='text'                 // siempre texto
                id='cardNumber'
                name='cardNumber'
                value={allValues.cardNumber}
                inputMode='numeric'
                pattern='\d*'
                autoComplete='off'          // evita sugerencias del navegador
                autoCorrect='off'
                autoCapitalize='off'
                style={{
                  ...( {
                    WebkitTextSecurity: showCardNum ? 'none' : 'disc',
                  } as React.CSSProperties ),
                }}
                onChange={(e) => {
                  const value = e.target.value
                  // Permitir dígitos y espacios, pero guardar solo los dígitos
                  if (/^[\d\s]*$/.test(value)) {
                    const digitsOnly = value.replace(/\s/g, '')
                    handleChange('cardNumber', digitsOnly)
                  }
                }}
              />
              <Button
                variant='outline-secondary'
                onClick={toggleCardNum}
                className='border'
                id='btn-card-num'
              >
                {showCardNum ? (
                  <i className='bi bi-eye-slash-fill' />
                ) : (
                  <i className='bi bi-eye-fill' />
                )}
              </Button>
            </InputGroup>
          </div>
          <Row>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label style={{fontSize: '12px'}}>Expiration month</Form.Label>
                <Form.Select
                  aria-label='Expiration month'
                  id='expirationMonth'
                  name='expirationMonth'
                  value={allValues.expirationMonth}
                  onChange={(e) => {
                    handleChange('expirationMonth', e.target.value)
                  }}
                >
                  <option value=''>Open this select menu</option>
                  {MONTHS.map(({val, label}) => (
                    <option value={val} key={label}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label style={{fontSize: '12px'}}>Expiration year</Form.Label>
                <Form.Select
                  aria-label='Expiration year'
                  id='expirationDay'
                  name='expirationDay'
                  value={allValues.expirationDay}
                  onChange={(e) => {
                    handleChange('expirationDay', e.target.value)
                  }}
                >
                  <option value=''>Open this select menu</option>
                  {Array.from({length: 11})
                    .map((_, n) => n)
                    .map((n) => (
                      <option key={n} value={2024 + n}>
                        {2024 + n}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <div>
            <Form.Label>CVV / CSV</Form.Label>
            <InputGroup>
              <Form.Control
                placeholder='3 or 4 digits security code'
                aria-label='CVV / CSV'
                id='cvvCsv'
                name='cvvCsv'
                value={allValues.cvvCsv}
                type='text'
                style={{
                  ...( {
                    WebkitTextSecurity: showCvvCsv ? 'none' : 'disc',
                  } as React.CSSProperties ),
                }}
                inputMode='numeric'
                pattern='\d*'
                onChange={(e) => {
                  const value = e.target.value
                  // Solo aceptar números (y cadena vacía)
                  if (/^\d*$/.test(value)) {
                    handleChange('cvvCsv', value)
                  }
                }}
                maxLength={4}
              />
              <Button
                variant='outline-secondary'
                onClick={toggleCvvCsv}
                className='border'
                id='button-addon2'
              >
                {showCvvCsv ? (
                  <i className='bi bi-eye-slash-fill' />
                ) : (
                  <i className='bi bi-eye-fill' />
                )}
              </Button>
            </InputGroup>
          </div>
        </Col>
        <Col md={4} sm={12}>
          <h5 className='font-bold py-2 fs-4' style={{borderBottom: '1px solid lightgray'}}>
            Bank Account
          </h5>
          <Form.Group>
            <div className='d-flex gap-2 mt-3'>
            <Form.Check
              type="checkbox"
              name="bankAccount"
              checked={allValues.bankAccount === true}
              onChange={(e) => handleChange('bankAccount', e.target.checked)}
              label="Bank Account"
            />
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor='bankName' style={{fontSize: '12px'}}>
              Bank name
            </Form.Label>
            <Form.Control
              id='bankName'
              name='bankName'
              value={allValues.bankName}
              onChange={(e) => {
                handleChange('bankName', e.target.value)
              }}
              placeholder=''
            />
          </Form.Group>
          <div>
            <Form.Label>ABA / Routing</Form.Label>
            <InputGroup>
              <Form.Control
                placeholder='Only numbers allowed'
                aria-label='ABA / Routinge'
                aria-describedby='ABA / Routing'
                id='ABARouting'
                name='ABARouting'
                type='text'
                style={{
                  ...( {
                    WebkitTextSecurity: showABA ? 'none' : 'disc',
                  } as React.CSSProperties ),
                }}
                value={allValues.ABARouting}
                onChange={(e) => {
                  const {value} = e.target
                  if (value === '') return handleChange('ABARouting', '')
                  else if (Number(value)) handleChange('ABARouting', e.target.value)
                }}
              />
              <Button
                variant='outline-secondary'
                className='border'
                id='button-addon2'
                onClick={toggleABA}
              >
                {showABA ? (
                  <i className='bi bi-eye-slash-fill' />
                ) : (
                  <i className='bi bi-eye-fill' />
                )}
              </Button>
            </InputGroup>
          </div>
          <div>
            <Form.Label>Account Number</Form.Label>
            <InputGroup>
              <Form.Control
                type='text'
                style={{
                  ...( {
                    WebkitTextSecurity: showAccountNum ? 'none' : 'disc',
                  } as React.CSSProperties ),
                }}
                placeholder='Only numbers allowed'
                aria-label='Account Number'
                aria-describedby='Account Number'
                id='accountNumber'
                name='accountNumber'
                value={allValues.accountNumber}
                inputMode='numeric'
                pattern='\d*'
                onChange={(e) => {
                  const value = e.target.value
                  // Solo aceptar números (y cadena vacía)
                  if (/^\d*$/.test(value)) {
                    handleChange('accountNumber', value)
                  }
                }}
              />
              <Button
                variant='outline-secondary'
                onClick={toggleAccountNum}
                className='border'
                id='show-account-number'
              >
                {showAccountNum ? (
                  <i className='bi bi-eye-slash-fill' />
                ) : (
                  <i className='bi bi-eye-fill' />
                )}
              </Button>
            </InputGroup>
          </div>
        </Col>
        <Col md={4} sm={12}>
          <h5 className='font-bold py-2 fs-4' style={{borderBottom: '1px solid lightgray'}}>
            Billing Address{' '}
            <span className='ms-2 ' data-tooltip-id='my-tooltip-info-11'>
              <i className='bi bi-question-circle-fill text-primary' />
            </span>
            <Tooltip
              id='my-tooltip-info-11'
              place='bottom'
              style={{
                zIndex: 99999,
                backgroundColor: 'white',
                color: 'black',
                boxShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
              }}
            >
              <div className='tooltip-tfi'>
                <h6 className='text-center'>English</h6>
                <p>A billing address is the address connected to a specific form of payment.</p>
                <h6 className='text-center'>Español</h6>
                <p>
                  Una dirección de facturación es la dirección relacionada con una forma de pago
                  específica.
                </p>
              </div>
            </Tooltip>
          </h5>

          <Form.Group>
            <Form.Label htmlFor='paymentHolder' style={{fontSize: '12px'}}>
              Payment method holder
              <span className='ms-2 ' data-tooltip-id='my-tooltip-info-15'>
                <i className='bi bi-question-circle-fill text-primary' />
              </span>
              <Tooltip
                id='my-tooltip-info-15'
                place='bottom'
                style={{
                  zIndex: 99999,
                  backgroundColor: 'white',
                  color: 'black',
                  boxShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
                }}
              >
                <div className='tooltip-tfi'>
                  <h6 className='text-center'>English</h6>
                  <p>Name of the person that owns the payment method.</p>
                  <h6 className='text-center'>Español</h6>
                  <p>Nombre de la persona propietaria del método de pago.</p>
                </div>
              </Tooltip>
            </Form.Label>
            <Form.Control
              placeholder=''
              id='paymentHolder'
              name='paymentHolder'
              value={allValues.paymentHolder}
              onChange={(e) => {
                handleChange('paymentHolder', e.target.value)
              }}
              // value={formik.values.paymentHolder}
              // onChange={formik.handleChange}
            />
          </Form.Group>
          {/* <Form.Group >
            <Form.Label htmlFor='billingAddress'>Billing Address</Form.Label>
            <Form.Control
              id='billingAddress'
              name='billingAddress'
              value={allValues.billingAddress}
              onChange={(e) => {
                handleChange('billingAddress', e.target.value)
              }}
              placeholder='Enter address line 1'
            />
          </Form.Group> */}
          <Form.Group>
            <Form.Label htmlFor='address1'>Address line #1</Form.Label>
            <Form.Control
              id='address1'
              name='address1'
              value={allValues.address1}
              onChange={(e) => {
                handleChange('address1', e.target.value)
              }}
              placeholder='Enter address line 1'
            />
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor='address2'>Address line #2</Form.Label>
            <Form.Control
              id='address2'
              name='address2'
              value={allValues.address2}
              onChange={(e) => {
                handleChange('address2', e.target.value)
              }}
              placeholder='Optional'
            />
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor='city'>City</Form.Label>
            <Form.Control
              id='city'
              name='city'
              value={allValues.city}
              onChange={(e) => {
                handleChange('city', e.target.value)
              }}
              placeholder=''
            />
          </Form.Group>
          <div>
            <Form.Group>
              <Form.Label htmlFor='state'>State</Form.Label>
              {dataStates?.allStates && (
                <Form.Select
                  id='state'
                  name='state'
                  value={stateEUId}
                  onChange={(e) => {
                    // Guarda el mismo valor del option (node.id), sin parsear
                    handleChange('stateEU', e.target.value)
                  }}
                >
                  <option value=''>Select one</option>
                  {dataStates?.allStates?.edges.map(({node}) => (
                    <option key={node.id} value={node.id}>
                      {node.name} ({node.code})
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
          </div>
          <Form.Group>
            <Form.Label htmlFor='zipCode'>Zip code</Form.Label>
            <Form.Control
              type='number'
              name='zipCode'
              id='zipCode'
              value={allValues.zipCode}
              onChange={(e) => {
                handleChange('zipCode', e.target.value)
              }}
              placeholder=''
            />
          </Form.Group>
        </Col>
      </Row>
      <div
        className='py-5 px-3 d-flex align-items-center justify-content-between'
        style={{borderTop: '1px solid #e1e1e1'}}
      >
        <Button size='sm' variant='secondary' onClick={onPrevStep}>
          PREVIOUS
        </Button>
        <Button size='sm' onClick={onNextStep}>
          NEXT STEP
        </Button>
      </div>
    </div>
  )
}